import React from 'react';
import { CheckCircle2, RefreshCw, Radio, Sparkles } from 'lucide-react';

const SUBMISSION_STAGES = [
  { id: 'validating', label: 'Checking release information' },
  { id: 'uploading_assets', label: 'Uploading master audio & artwork assets' },
  { id: 'saving_metadata', label: 'Saving track metadata & songwriter credits' },
  { id: 'registering', label: 'Assigning SenritsuX Registration Number' },
  { id: 'finalizing', label: 'Finalizing submission & A&R registration' },
];

export default function SubmissionProgressModal({
  currentStage = 'validating',
  currentLabel = '',
  currentFile = 0,
  totalFiles = 0,
  percent = 0,
}) {
  const currentStageIndex = SUBMISSION_STAGES.findIndex((s) => s.id === currentStage);
  const activeIndex = currentStageIndex >= 0 ? currentStageIndex : 0;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(10, 8, 30, 0.82)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: 'linear-gradient(145deg, #383494, #26236b)',
          border: '1px solid rgba(217, 251, 6, 0.4)',
          borderRadius: '16px',
          padding: '2rem',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.7)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'rgba(217, 251, 6, 0.15)',
            color: '#d9fb06',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.2rem',
            border: '2px solid rgba(217, 251, 6, 0.3)',
          }}
        >
          <Radio size={32} className="animate-pulse" />
        </div>

        <h3 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#ffffff', margin: '0 0 0.4rem' }}>
          Processing Release Submission
        </h3>
        <p style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.75)', margin: '0 0 1.5rem' }}>
          Please do not close or refresh your browser while we secure your master audio and metadata.
        </p>

        {/* Per-File Progress Indicator when uploading assets */}
        {currentStage === 'uploading_assets' && currentLabel && (
          <div
            style={{
              background: 'rgba(0, 0, 0, 0.35)',
              border: '1px solid rgba(217, 251, 6, 0.25)',
              borderRadius: '10px',
              padding: '0.8rem 1rem',
              marginBottom: '1.2rem',
              textAlign: 'left',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
              <span style={{ fontSize: '0.82rem', color: '#d9fb06', fontWeight: 600 }}>
                {currentLabel}
              </span>
              {percent > 0 && (
                <span style={{ fontSize: '0.78rem', color: '#ffffff', fontWeight: 700 }}>
                  {percent}%
                </span>
              )}
            </div>
            <div
              style={{
                width: '100%',
                height: '6px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '3px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${Math.min(100, Math.max(0, percent || 10))}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #d9fb06, #a3e635)',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        )}

        {/* Step-by-Step Status Checklist */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', textAlign: 'left', background: 'rgba(0,0,0,0.25)', padding: '1.2rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)' }}>
          {SUBMISSION_STAGES.map((stage, idx) => {
            const isCompleted = idx < activeIndex;
            const isCurrent = idx === activeIndex;
            const isPending = idx > activeIndex;

            return (
              <div
                key={stage.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  color: isCompleted ? '#d9fb06' : isCurrent ? '#ffffff' : 'rgba(255, 255, 255, 0.35)',
                  fontWeight: isCurrent ? 600 : 400,
                  fontSize: '0.86rem',
                  transition: 'all 0.3s ease',
                }}
              >
                {isCompleted ? (
                  <CheckCircle2 size={18} color="#d9fb06" />
                ) : isCurrent ? (
                  <RefreshCw size={17} color="#d9fb06" className="animate-spin" />
                ) : (
                  <div
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      border: '1.5px solid rgba(255,255,255,0.2)',
                    }}
                  />
                )}
                <span>{stage.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
